import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const StoreMetadataEditor = ({ store, clientId, onSave, onClose }) => {
  const [category, setCategory] = useState(store.category || '');
  const [segment, setSegment] = useState(store.segment || '');
  const [tagsInput, setTagsInput] = useState((store.tags || []).join(', '));
  const [isActive, setIsActive] = useState(store.is_active ?? true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  // Taxonomy state
  const [taxonomy, setTaxonomy] = useState({ types: [], categories: [], segments: [] });
  const [taxonomyLoading, setTaxonomyLoading] = useState(true);

  // Fetch taxonomy on mount
  useEffect(() => {
    const fetchTaxonomy = async () => {
      try {
        const response = await fetch(`${API_URL}/api/admin/unified-clients/${clientId}/taxonomy`);
        if (response.ok) {
          const data = await response.json();
          setTaxonomy(data);
        }
      } catch (err) {
        console.error('Failed to fetch taxonomy:', err);
      } finally {
        setTaxonomyLoading(false);
      }
    };
    if (clientId) {
      fetchTaxonomy();
    } else {
      setTaxonomyLoading(false);
    }
  }, [clientId]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Parse tags from comma-separated input
      const tags = tagsInput
        .split(',')
        .map(t => t.trim())
        .filter(t => t.length > 0);

      await onSave(store.id, {
        category: category || null,
        segment: segment || null,
        tags,
        is_active: isActive
      });
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4"
        data-testid="store-metadata-editor"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Edit Store Metadata</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            data-testid="close-editor"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-4">
          {/* Read-only fields */}
          <div className="bg-gray-50 rounded-lg p-3 space-y-2">
            <div className="text-sm">
              <span className="text-gray-500">Store:</span>{' '}
              <span className="font-medium text-gray-900">{store.name}</span>
            </div>
            <div className="text-sm">
              <span className="text-gray-500">ID:</span>{' '}
              <span className="font-mono text-gray-700">{store.id}</span>
            </div>
            <div className="text-sm">
              <span className="text-gray-500">Type:</span>{' '}
              <span className="text-gray-700">{store.type || '—'}</span>
            </div>
            <div className="text-sm">
              <span className="text-gray-500">Group:</span>{' '}
              <span className="text-gray-700">{store.metadata?.store_group || '—'}</span>
            </div>
          </div>

          <hr className="border-gray-200" />

          {/* Editable fields */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="input-category"
              disabled={taxonomyLoading}
            >
              <option value="">— No category —</option>
              {taxonomy.categories.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Segment
            </label>
            <select
              value={segment}
              onChange={(e) => setSegment(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="input-segment"
              disabled={taxonomyLoading}
            >
              <option value="">— No segment —</option>
              {taxonomy.segments.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tags
            </label>
            <input
              type="text"
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              placeholder="seasonal, flagship, new (comma-separated)"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="input-tags"
            />
            <p className="text-xs text-gray-500 mt-1">Separate multiple tags with commas</p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="is_active"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                data-testid="input-is-active"
              />
              <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                Active
              </label>
            </div>
            {!isActive && (
              <div className="p-2 bg-amber-50 border border-amber-200 rounded text-sm text-amber-700">
                Inactive stores remain in their groups but may be hidden from some views.
              </div>
            )}
          </div>

          {/* Error */}
          {error && (
            <div className="p-3 bg-red-50 text-red-600 text-sm rounded">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-gray-700 hover:text-gray-900 disabled:opacity-50"
            data-testid="cancel-edit"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="save-store-metadata"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StoreMetadataEditor;
